/**
 * 서명 보관함 다이얼로그 — 모든 서명 카드 + 미리보기 + 기본설정/편집/삭제/새로 만들기.
 */
import React, { useState } from "react";
import {
    Box, Button, Card, CardContent, IconButton, Stack, Tooltip, Typography,
} from "@mui/material";
import StarRoundedIcon       from "@mui/icons-material/StarRounded";
import StarBorderRoundedIcon from "@mui/icons-material/StarBorderRounded";
import EditRoundedIcon       from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon     from "@mui/icons-material/DeleteRounded";
import AddRoundedIcon        from "@mui/icons-material/AddRounded";

import { toSignaturePreviewHtml } from "./signaturePreview";
import { mailApi } from "../../api/mailApi";
import { ds }      from "../../../../styles/designSystem";
import { notify }  from "../../../../util/toast";
import { confirm } from "../../../../util/confirmDialog";
import FormModal   from "../../../../component/common/FormModal";
import ActionButton from "../../../../component/common/ActionButton";

export default function SignatureGalleryDialog({
    open, onClose,
    signatures = [],
    onReload, onEdit, onCreate,
}) {
    const [busy, setBusy] = useState(false);

    const handleSetDefault = async (id) => {
        if (busy) return;
        setBusy(true);
        try {
            await mailApi.setDefaultSignature(id);
            notify("기본 서명으로 설정했습니다", "success");
            await onReload?.();
        } catch {
            notify("기본 서명 설정 실패", "error");
        } finally { setBusy(false); }
    };

    const handleDelete = async (id) => {
        const ok = await confirm({
            title: "서명 삭제",
            message: "이 서명을 삭제할까요?",
            confirmLabel: "삭제",
            variant: "danger",
        });
        if (!ok) return;
        setBusy(true);
        try {
            await mailApi.deleteSignature(id);
            await onReload?.();
        } catch {
            notify("서명 삭제 실패", "error");
        } finally { setBusy(false); }
    };

    return (
        <FormModal
            open={open}
            onClose={onClose}
            title="서명 보관함"
            subtitle={`총 ${signatures.length}개`}
            maxWidth="md"
            cancelLabel="닫기"
            headerExtras={
                <ActionButton variant="add" onClick={onCreate} label="새 서명" />
            }
        >
            {signatures.length === 0 ? (
                <Box sx={{ py: 6, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
                    저장된 서명이 없습니다. "새 서명" 으로 시작하세요.
                </Box>
            ) : (
                <Stack spacing={1.5}>
                    {signatures.map((s) => (
                        <Card key={s.id} variant="outlined" sx={{ borderColor: s.isDefault ? "#f59e0b" : "#e2e8f0" }}>
                            <CardContent sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
                                            {s.name || "(이름 없음)"}
                                        </Typography>
                                        {s.isDefault && (
                                            <Box sx={{ display: "inline-flex", alignItems: "center" }}>
                                                <StarRoundedIcon sx={{ fontSize: 14, color: "#f59e0b" }} />
                                                <Typography sx={{ fontSize: 10, ml: 0.3, color: "#f59e0b", fontWeight: 700 }}>기본</Typography>
                                            </Box>
                                        )}
                                        {/* 작성 방식 — 편집을 열기 전에 어느 탭으로 열릴지 미리 알 수 있게. */}
                                        <Typography sx={{
                                            fontSize: 10, fontWeight: 700, color: "#64748b",
                                            bgcolor: "#f1f5f9", px: 0.7, py: 0.1, borderRadius: 0.5,
                                        }}>
                                            {(s.bodyType === "BASIC" || s.card) ? "기본"
                                                : s.bodyType === "TEXT" ? "기본 텍스트" : "HTML"}
                                        </Typography>
                                    </Stack>
                                    {/* 앞뒤 빈 줄을 걷어내고 보여 준다 — 외부 편집기에서 옮겨온 서명은
                                        간격용 <p><br></p> 가 앞에 잔뜩 붙어 있어 그대로 두면
                                        이 좁은 칸에 빈 줄만 보이다 잘린다. */}
                                    <Box
                                        sx={{
                                            fontSize: 12, color: "#475569",
                                            p: 1, bgcolor: "#fff", border: "1px solid #e2e8f0", borderRadius: 1,
                                            maxHeight: 180, overflow: "auto",
                                            "& img": { maxWidth: "100%" },
                                            "& table": { maxWidth: "100%" },
                                        }}
                                        dangerouslySetInnerHTML={{
                                            __html: toSignaturePreviewHtml(s.bodyHtml),
                                        }}
                                    />
                                </Box>
                                <Stack spacing={0.5}>
                                    <Tooltip title={s.isDefault ? "이미 기본 서명" : "기본 서명으로 설정"}>
                                        <span>
                                            <IconButton size="small" disabled={s.isDefault || busy}
                                                onClick={() => handleSetDefault(s.id)}>
                                                {s.isDefault
                                                    ? <StarRoundedIcon sx={{ fontSize: 18, color: "#f59e0b" }} />
                                                    : <StarBorderRoundedIcon sx={{ fontSize: 18, color: "#94a3b8" }} />}
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="편집">
                                        <IconButton size="small" onClick={() => onEdit?.(s)}>
                                            <EditRoundedIcon sx={{ fontSize: 18, color: "#64748b" }} />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="삭제">
                                        <IconButton size="small" onClick={() => handleDelete(s.id)} disabled={busy}>
                                            <DeleteRoundedIcon sx={{ fontSize: 18, color: "#ef4444" }} />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </CardContent>
                        </Card>
                    ))}
                </Stack>
            )}
        </FormModal>
    );
}
